import type {
  T_ChatV8Capabilities,
  T_ChatV8Event,
  T_ChatV8Run,
  T_ChatV8ThreadPage,
} from "@lattice/core-lib/types/chat-v8";
import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, setup } from "xstate";

import { mergeChatV8Events } from "@lattice/core-lib/chat-v8/materialize";

export type T_ChatV8ClientStatus =
  | "idle"
  | "loadingThread"
  | "ready"
  | "sending"
  | "streaming"
  | "waitingForElicitation"
  | "waitingForApproval"
  | "error";

export type T_ChatV8ClientContext = {
  activeRun: T_ChatV8Run | null;
  capabilities: T_ChatV8Capabilities | null;
  error: string | null;
  events: T_ChatV8Event[];
  hasMoreBefore: boolean;
  latestDisplaySeq: number;
  latestSeq: number;
  newestDisplaySeq: number | null;
  oldestDisplaySeq: number | null;
  status: T_ChatV8ClientStatus;
  streamStartedAt: string | null;
  subscriptionError: string | null;
  threadCuid: string | null;
  threadName: string | null;
  timelineVersion: number | null;
};

export type T_ChatV8ClientEvent =
  | { type: "CAPABILITIES_LOADED"; capabilities: T_ChatV8Capabilities }
  | { type: "EVENTS_RECEIVED"; events: T_ChatV8Event[]; latestSeq?: number }
  | { type: "LOAD_OLDER_OK"; page: T_ChatV8ThreadPage }
  | { type: "MUTATION_ERROR"; error: string }
  | { type: "REQUEST_STARTED"; status: T_ChatV8ClientStatus }
  | { type: "RESET_THREAD" }
  | { type: "RUN_UPDATED"; run: T_ChatV8Run | null }
  | { type: "SUBSCRIPTION_ERROR"; error: string }
  | { type: "SUBSCRIPTION_STARTED"; startedAt: string }
  | {
      type: "SEND_OK";
      result: {
        run: T_ChatV8Run;
        threadCuid: string;
        timelineVersion: number;
        userEvent: T_ChatV8Event;
      };
    }
  | { type: "THREAD_LOAD_ERROR"; error: string }
  | { type: "THREAD_LOAD_OK"; page: T_ChatV8ThreadPage }
  | { type: "THREAD_LOAD_STARTED"; threadCuid: string };

export const initialChatV8ClientContext: T_ChatV8ClientContext = {
  activeRun: null,
  capabilities: null,
  error: null,
  events: [],
  hasMoreBefore: false,
  latestDisplaySeq: 0,
  latestSeq: 0,
  newestDisplaySeq: null,
  oldestDisplaySeq: null,
  status: "idle",
  streamStartedAt: null,
  subscriptionError: null,
  threadCuid: null,
  threadName: null,
  timelineVersion: null,
};

const chatV8ClientSetupTypes: SetupTypes<
  T_ChatV8ClientContext,
  T_ChatV8ClientEvent,
  Record<never, string>,
  string,
  Record<never, never>,
  unknown,
  EventObject,
  MetaObject
> = {};

export const chatV8ClientMachine = setup({
  types: chatV8ClientSetupTypes,
  actions: {
    applyCapabilities: assign(({ event }) => {
      if (event.type !== "CAPABILITIES_LOADED") return {};
      return { capabilities: event.capabilities };
    }),
    applyIncomingEvents: assign(({ context, event }) => {
      if (event.type !== "EVENTS_RECEIVED") return {};
      return applyIncomingEvents({
        events: event.events,
        latestSeq: event.latestSeq,
        state: context,
      });
    }),
    applyMutationError: assign(({ event }) => {
      if (event.type !== "MUTATION_ERROR") return {};
      return {
        error: event.error,
        status: "error" satisfies T_ChatV8ClientStatus,
      };
    }),
    applyOlderPage: assign(({ context, event }) => {
      if (event.type !== "LOAD_OLDER_OK") return {};
      return {
        events: mergeChatV8Events(event.page.events, context.events),
        hasMoreBefore: event.page.hasMoreBefore,
        oldestDisplaySeq: event.page.oldestDisplaySeq,
      };
    }),
    applyRunUpdated: assign(({ event }) => {
      if (event.type !== "RUN_UPDATED") return {};
      return {
        activeRun: event.run,
        status: getStatusForChatV8Run(event.run),
      };
    }),
    applySendOk: assign(({ context, event }) => {
      if (event.type !== "SEND_OK") return {};
      return {
        activeRun: event.result.run,
        error: null,
        events: mergeChatV8Events(context.events, [event.result.userEvent]),
        latestSeq: Math.max(context.latestSeq, event.result.userEvent.seq),
        status: getStatusForChatV8Run(event.result.run),
        threadCuid: event.result.threadCuid,
        timelineVersion: event.result.timelineVersion,
      };
    }),
    applySubscriptionError: assign(({ context, event }) => {
      if (event.type !== "SUBSCRIPTION_ERROR") return {};
      return {
        status:
          context.status === "loadingThread"
            ? ("error" satisfies T_ChatV8ClientStatus)
            : context.status,
        subscriptionError: event.error,
      };
    }),
    applySubscriptionStarted: assign(({ event }) => {
      if (event.type !== "SUBSCRIPTION_STARTED") return {};
      return {
        streamStartedAt: event.startedAt,
        subscriptionError: null,
      };
    }),
    applyThreadLoadError: assign(({ event }) => {
      if (event.type !== "THREAD_LOAD_ERROR") return {};
      return {
        error: event.error,
        status: "error" satisfies T_ChatV8ClientStatus,
      };
    }),
    applyThreadLoadOk: assign(({ context, event }) => {
      if (event.type !== "THREAD_LOAD_OK") return {};
      const loadedHistory = getLoadedHistoryBeforeRefreshedTail({
        page: event.page,
        state: context,
      });
      return {
        activeRun: event.page.activeRun,
        error: null,
        events: mergeChatV8Events(loadedHistory.events, event.page.events),
        hasMoreBefore:
          loadedHistory.oldestDisplaySeq === null
            ? event.page.hasMoreBefore
            : context.hasMoreBefore,
        latestDisplaySeq: event.page.latestDisplaySeq,
        latestSeq: event.page.latestSeq,
        newestDisplaySeq: event.page.newestDisplaySeq,
        oldestDisplaySeq:
          loadedHistory.oldestDisplaySeq ?? event.page.oldestDisplaySeq,
        status: getStatusForChatV8Run(event.page.activeRun),
        subscriptionError: null,
        threadCuid: event.page.threadCuid,
        threadName: event.page.threadName,
        timelineVersion: event.page.timelineVersion,
      };
    }),
    beginRequest: assign(({ event }) => {
      if (event.type !== "REQUEST_STARTED") return {};
      return {
        error: null,
        status: event.status,
      };
    }),
    beginThreadLoad: assign(({ context, event }) => {
      if (event.type !== "THREAD_LOAD_STARTED") return {};
      // Stale-while-revalidate: reloading the thread the user is already
      // reading (run.completed reconciliation, reconnect catch-up, timeline
      // repair) must NOT blank the transcript — clearing events here made
      // the whole conversation flash to a spinner every time a response
      // finished streaming (James's device QA, 2026-07-13). Keep rendering
      // the current events; THREAD_LOAD_OK reconciles the authoritative tail
      // while preserving any explicitly paged-in history. Only a different
      // thread or the first load of this thread resets to the loading state.
      if (
        context.threadCuid === event.threadCuid &&
        context.timelineVersion !== null
      ) {
        return { error: null, subscriptionError: null };
      }
      return {
        activeRun: null,
        error: null,
        events: [],
        hasMoreBefore: false,
        latestDisplaySeq: 0,
        latestSeq: 0,
        newestDisplaySeq: null,
        oldestDisplaySeq: null,
        status: "loadingThread" satisfies T_ChatV8ClientStatus,
        subscriptionError: null,
        threadCuid: event.threadCuid,
        threadName: null,
        timelineVersion: null,
      };
    }),
    resetThread: assign(({ context }) => ({
      ...initialChatV8ClientContext,
      capabilities: context.capabilities,
    })),
  },
  guards: {
    eventRunIsReady: ({ event }) => getEventRunStatus(event) === "ready",
    eventRunIsStreaming: ({ event }) =>
      getEventRunStatus(event) === "streaming",
    eventRunIsWaitingForApproval: ({ event }) =>
      getEventRunStatus(event) === "waitingForApproval",
    eventRunIsWaitingForElicitation: ({ event }) =>
      getEventRunStatus(event) === "waitingForElicitation",
    incomingEventsAreReady: ({ event }) =>
      getIncomingEventRunStatus(event) === "ready",
    incomingEventsAreStreaming: ({ event }) =>
      getIncomingEventRunStatus(event) === "streaming",
    incomingEventsAreWaitingForApproval: ({ event }) =>
      getIncomingEventRunStatus(event) === "waitingForApproval",
    incomingEventsAreWaitingForElicitation: ({ event }) =>
      getIncomingEventRunStatus(event) === "waitingForElicitation",
    incomingEventsShouldStream: ({ context, event }) =>
      event.type === "EVENTS_RECEIVED" &&
      hasIncomingEventProgress({
        events: event.events,
        latestSeq: event.latestSeq,
        state: context,
      }),
    requestStatusIsError: ({ event }) =>
      event.type === "REQUEST_STARTED" && event.status === "error",
    requestStatusIsIdle: ({ event }) =>
      event.type === "REQUEST_STARTED" && event.status === "idle",
    requestStatusIsLoadingThread: ({ event }) =>
      event.type === "REQUEST_STARTED" && event.status === "loadingThread",
    requestStatusIsReady: ({ event }) =>
      event.type === "REQUEST_STARTED" && event.status === "ready",
    requestStatusIsSending: ({ event }) =>
      event.type === "REQUEST_STARTED" && event.status === "sending",
    requestStatusIsStreaming: ({ event }) =>
      event.type === "REQUEST_STARTED" && event.status === "streaming",
    requestStatusIsWaitingForApproval: ({ event }) =>
      event.type === "REQUEST_STARTED" && event.status === "waitingForApproval",
    requestStatusIsWaitingForElicitation: ({ event }) =>
      event.type === "REQUEST_STARTED" &&
      event.status === "waitingForElicitation",
  },
}).createMachine({
  id: "chatV8Client",
  context: initialChatV8ClientContext,
  initial: "idle",
  on: {
    CAPABILITIES_LOADED: { actions: "applyCapabilities" },
    EVENTS_RECEIVED: [
      {
        guard: "incomingEventsAreReady",
        target: ".ready",
        actions: "applyIncomingEvents",
      },
      {
        guard: "incomingEventsAreWaitingForElicitation",
        target: ".waitingForElicitation",
        actions: "applyIncomingEvents",
      },
      {
        guard: "incomingEventsAreWaitingForApproval",
        target: ".waitingForApproval",
        actions: "applyIncomingEvents",
      },
      {
        guard: "incomingEventsAreStreaming",
        target: ".streaming",
        actions: "applyIncomingEvents",
      },
      {
        guard: "incomingEventsShouldStream",
        target: ".streaming",
        actions: "applyIncomingEvents",
      },
      {
        actions: "applyIncomingEvents",
      },
    ],
    LOAD_OLDER_OK: { actions: "applyOlderPage" },
    MUTATION_ERROR: { target: ".error", actions: "applyMutationError" },
    REQUEST_STARTED: [
      {
        guard: "requestStatusIsIdle",
        target: ".idle",
        actions: "beginRequest",
      },
      {
        guard: "requestStatusIsLoadingThread",
        target: ".loadingThread",
        actions: "beginRequest",
      },
      {
        guard: "requestStatusIsReady",
        target: ".ready",
        actions: "beginRequest",
      },
      {
        guard: "requestStatusIsSending",
        target: ".sending",
        actions: "beginRequest",
      },
      {
        guard: "requestStatusIsStreaming",
        target: ".streaming",
        actions: "beginRequest",
      },
      {
        guard: "requestStatusIsWaitingForElicitation",
        target: ".waitingForElicitation",
        actions: "beginRequest",
      },
      {
        guard: "requestStatusIsWaitingForApproval",
        target: ".waitingForApproval",
        actions: "beginRequest",
      },
      {
        guard: "requestStatusIsError",
        target: ".error",
        actions: "beginRequest",
      },
    ],
    RESET_THREAD: { target: ".idle", actions: "resetThread" },
    RUN_UPDATED: [
      {
        guard: "eventRunIsStreaming",
        target: ".streaming",
        actions: "applyRunUpdated",
      },
      {
        guard: "eventRunIsWaitingForElicitation",
        target: ".waitingForElicitation",
        actions: "applyRunUpdated",
      },
      {
        guard: "eventRunIsWaitingForApproval",
        target: ".waitingForApproval",
        actions: "applyRunUpdated",
      },
      {
        guard: "eventRunIsReady",
        target: ".ready",
        actions: "applyRunUpdated",
      },
    ],
    SEND_OK: [
      {
        guard: "eventRunIsStreaming",
        target: ".streaming",
        actions: "applySendOk",
      },
      {
        guard: "eventRunIsWaitingForElicitation",
        target: ".waitingForElicitation",
        actions: "applySendOk",
      },
      {
        guard: "eventRunIsWaitingForApproval",
        target: ".waitingForApproval",
        actions: "applySendOk",
      },
      { target: ".ready", actions: "applySendOk" },
    ],
    SUBSCRIPTION_ERROR: { actions: "applySubscriptionError" },
    SUBSCRIPTION_STARTED: { actions: "applySubscriptionStarted" },
    THREAD_LOAD_ERROR: {
      target: ".error",
      actions: "applyThreadLoadError",
    },
    THREAD_LOAD_OK: [
      {
        guard: "eventRunIsStreaming",
        target: ".streaming",
        actions: "applyThreadLoadOk",
      },
      {
        guard: "eventRunIsWaitingForElicitation",
        target: ".waitingForElicitation",
        actions: "applyThreadLoadOk",
      },
      {
        guard: "eventRunIsWaitingForApproval",
        target: ".waitingForApproval",
        actions: "applyThreadLoadOk",
      },
      { target: ".ready", actions: "applyThreadLoadOk" },
    ],
    THREAD_LOAD_STARTED: {
      target: ".loadingThread",
      actions: "beginThreadLoad",
    },
  },
  states: {
    error: {},
    idle: {},
    loadingThread: {
      on: {
        SUBSCRIPTION_ERROR: {
          target: "error",
          actions: "applySubscriptionError",
        },
      },
    },
    ready: {},
    sending: {},
    streaming: {},
    waitingForApproval: {},
    waitingForElicitation: {},
  },
});

export type T_ChatV8ClientSnapshotLike = {
  context: T_ChatV8ClientContext;
};

export function getChatV8ClientViewState(
  snapshot: T_ChatV8ClientSnapshotLike,
): T_ChatV8ClientContext {
  return snapshot.context;
}

export function isChatV8ThreadRoutePending(args: {
  expectedThreadCuid: string | null;
  state: Pick<
    T_ChatV8ClientContext,
    "status" | "threadCuid" | "timelineVersion"
  >;
}): boolean {
  return (
    args.expectedThreadCuid !== args.state.threadCuid ||
    (args.expectedThreadCuid !== null &&
      args.state.timelineVersion === null &&
      args.state.status !== "error")
  );
}

export function isChatV8InitialThreadLoadFailure(args: {
  expectedThreadCuid: string | null;
  state: Pick<
    T_ChatV8ClientContext,
    "status" | "threadCuid" | "timelineVersion"
  >;
}): boolean {
  return (
    args.expectedThreadCuid !== null &&
    args.state.threadCuid === args.expectedThreadCuid &&
    args.state.timelineVersion === null &&
    args.state.status === "error"
  );
}

export function canUseChatV8Composer(status: T_ChatV8ClientStatus): boolean {
  return (
    status !== "loadingThread" &&
    status !== "sending" &&
    status !== "waitingForApproval" &&
    status !== "waitingForElicitation"
  );
}

export function getChatV8ClientStatusLabel(status: T_ChatV8ClientStatus) {
  switch (status) {
    case "idle":
      return "Idle";
    case "loadingThread":
      return "Loading";
    case "ready":
      return "Ready";
    case "sending":
      return "Sending";
    case "streaming":
      return "Streaming";
    case "waitingForApproval":
      return "Approval";
    case "waitingForElicitation":
      return "Input";
    case "error":
      return "Error";
    default:
      return status;
  }
}

export function getStatusForChatV8Run(
  run: T_ChatV8Run | null,
): T_ChatV8ClientStatus {
  if (!run) {
    return "ready";
  }

  switch (run.status) {
    case "CREATED":
    case "RUNNING":
      return "streaming";
    case "WAITING_FOR_APPROVAL":
      return "waitingForApproval";
    case "WAITING_FOR_INPUT":
      return "waitingForElicitation";
    case "CANCELLED":
    case "COMPLETED":
    case "FAILED":
      return "ready";
    default:
      return "ready";
  }
}

function applyIncomingEvents(args: {
  events: T_ChatV8Event[];
  latestSeq?: number;
  state: T_ChatV8ClientContext;
}): Partial<T_ChatV8ClientContext> {
  const existingEventCuids = new Set(
    args.state.events.map((event) => event.eventCuid),
  );
  const hasNewEvent = args.events.some(
    (event) => !existingEventCuids.has(event.eventCuid),
  );
  if (
    !hasNewEvent &&
    (args.latestSeq === undefined || args.latestSeq <= args.state.latestSeq)
  ) {
    return {};
  }

  const mergedEvents = mergeChatV8Events(args.state.events, args.events);
  const latestIncomingSeq = getLatestEventSeq(args.events);
  const latestSeq = Math.max(
    args.state.latestSeq,
    args.latestSeq ?? 0,
    latestIncomingSeq,
  );
  const lifecycleStatus = getLatestRunLifecycleStatus(args.events);
  const status =
    lifecycleStatus ??
    (args.state.status === "sending" || args.state.status === "ready"
      ? "streaming"
      : args.state.status);

  return {
    activeRun: lifecycleStatus === "ready" ? null : args.state.activeRun,
    events: mergedEvents,
    latestSeq,
    status,
    subscriptionError: null,
  };
}

function getLoadedHistoryBeforeRefreshedTail(args: {
  page: T_ChatV8ThreadPage;
  state: T_ChatV8ClientContext;
}): {
  events: T_ChatV8Event[];
  oldestDisplaySeq: number | null;
} {
  const pageOldestDisplaySeq = args.page.oldestDisplaySeq;
  if (
    args.state.threadCuid !== args.page.threadCuid ||
    args.state.timelineVersion !== args.page.timelineVersion ||
    args.state.oldestDisplaySeq === null ||
    pageOldestDisplaySeq === null ||
    args.state.oldestDisplaySeq >= pageOldestDisplaySeq
  ) {
    return { events: [], oldestDisplaySeq: null };
  }

  const historicalDisplayEvents = args.state.events.filter(
    (event) =>
      event.displaySeq !== null && event.displaySeq < pageOldestDisplaySeq,
  );
  const historicalItemCuids = collectEventIdentifier(
    historicalDisplayEvents,
    "itemCuid",
  );
  const historicalPartCuids = collectEventIdentifier(
    historicalDisplayEvents,
    "partCuid",
  );
  const historicalToolCallCuids = collectEventIdentifier(
    historicalDisplayEvents,
    "toolCallCuid",
  );
  const historicalActionCuids = collectEventIdentifier(
    historicalDisplayEvents,
    "actionCuid",
  );

  return {
    events: args.state.events.filter((event) => {
      if (
        event.displaySeq !== null &&
        event.displaySeq < pageOldestDisplaySeq
      ) {
        return true;
      }
      if (event.displaySeq !== null) return false;
      if (event.partCuid !== null) {
        return historicalPartCuids.has(event.partCuid);
      }
      if (event.toolCallCuid !== null) {
        return historicalToolCallCuids.has(event.toolCallCuid);
      }
      if (event.actionCuid !== null) {
        return historicalActionCuids.has(event.actionCuid);
      }
      return event.itemCuid !== null && historicalItemCuids.has(event.itemCuid);
    }),
    oldestDisplaySeq: args.state.oldestDisplaySeq,
  };
}

function collectEventIdentifier(
  events: T_ChatV8Event[],
  key: "actionCuid" | "itemCuid" | "partCuid" | "toolCallCuid",
): Set<string> {
  const values = new Set<string>();
  for (const event of events) {
    const value = event[key];
    if (value !== null) values.add(value);
  }
  return values;
}

function getLatestRunLifecycleStatus(
  events: T_ChatV8Event[],
): T_ChatV8ClientStatus | null {
  let latestLifecycleEvent: T_ChatV8Event | null = null;

  for (const event of events) {
    if (!getRunLifecycleStatus(event)) {
      continue;
    }

    if (!latestLifecycleEvent || event.seq > latestLifecycleEvent.seq) {
      latestLifecycleEvent = event;
    }
  }

  return latestLifecycleEvent
    ? getRunLifecycleStatus(latestLifecycleEvent)
    : null;
}

function getRunLifecycleStatus(
  event: T_ChatV8Event,
): T_ChatV8ClientStatus | null {
  switch (event.type) {
    case "run.created":
    case "run.started":
      return "streaming";
    case "run.waiting_for_approval":
      return "waitingForApproval";
    case "run.waiting_for_input":
      return "waitingForElicitation";
    case "run.cancelled":
    case "run.completed":
    case "run.failed":
      return "ready";
    default:
      return null;
  }
}

function getEventRunStatus(event: T_ChatV8ClientEvent): T_ChatV8ClientStatus {
  switch (event.type) {
    case "RUN_UPDATED":
      return getStatusForChatV8Run(event.run);
    case "SEND_OK":
      return getStatusForChatV8Run(event.result.run);
    case "THREAD_LOAD_OK":
      return getStatusForChatV8Run(event.page.activeRun);
    default:
      return "ready";
  }
}

function getIncomingEventRunStatus(
  event: T_ChatV8ClientEvent,
): T_ChatV8ClientStatus | null {
  return event.type === "EVENTS_RECEIVED"
    ? getLatestRunLifecycleStatus(event.events)
    : null;
}

function hasIncomingEventProgress(args: {
  events: T_ChatV8Event[];
  latestSeq?: number;
  state: T_ChatV8ClientContext;
}) {
  if (args.latestSeq !== undefined && args.latestSeq > args.state.latestSeq) {
    return true;
  }

  const existingEventCuids = new Set(
    args.state.events.map((event) => event.eventCuid),
  );
  return args.events.some((event) => !existingEventCuids.has(event.eventCuid));
}

function getLatestEventSeq(events: T_ChatV8Event[]) {
  let latestSeq = 0;

  for (const event of events) {
    latestSeq = Math.max(latestSeq, event.seq);
  }

  return latestSeq;
}
